import React, { useState, useEffect, useMemo, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { Link } from "react-router-dom";
import Spinner from "components/Spinner";
import { PhGridPagination, PhGridFilter, usePagination, PhGridSort } from "components/PhGrid";

function calcDaysTilPurge(createdDate) {
  if (!createdDate) return "-";
  const purgeTime = createdDate.getTime() + 90 * 24 * 3600 * 1000; // +90 days
  const now = Date.now();
  const days = (purgeTime - now) / (1000 * 3600 * 24);
  return days < 0 ? "expired" : days < 1 ? "< 1 day" : `${Math.floor(days)} days`;
}

// https://ourcodeworld.com/articles/read/713/converting-bytes-to-human-readable-values-kb-mb-gb-tb-pb-eb-zb-yb-with-javascript
function readableBytes(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  return (bytes / Math.pow(1024, i)).toFixed(2) * 1 + " " + sizes[i];
}

function MyFilesContainer() {
  const {
    paginationOffset,
    setPaginationOffset,
    paginationSize,
    setPaginationSize,
    filterString,
    setFilterString,
    sortColumn,
    setSortColumn,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection,
  } = usePagination();

  const [files, setFiles] = useState(null);

  // Auth readiness + in-flight guard
  const [ready, setReady] = useState(false);
  const inFlight = useRef(false);

  // Determine when we actually have a session
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setFiles(null);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Fetch files only when authenticated
  useEffect(() => {
    if (!ready) return;
    if (inFlight.current) return;
    inFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check
        const filesResponse = await API.get("FileApi", `/filemanagement/files`);
        if (cancelled) return;

        const normalized = (filesResponse || []).map((f) => {
          const created = f.created_date ? new Date(f.created_date) : null;
          return { ...f, created_date: created };
        });
        setFiles(normalized);
      } catch (err) {
        if (!cancelled) console.error("error fetching files list", err);
      } finally {
        inFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  const displayList = useMemo(() => {
    if (!files) return [];
    return files
      .filter((file) =>
        [
          file.description,
          file.id,
          file.file_size ? readableBytes(file.file_size) : "unavailable",
          file.created_date ? file.created_date.toLocaleDateString() : "-",
          calcDaysTilPurge(file.created_date),
        ]
          .join("")
          .toLowerCase()
          .includes(filterString.toLowerCase())
      )
      .sort((a, b) => {
        if (!sortColumn) return 0;
        if (sortDirection === "ascending") {
          return sortKey(a) > sortKey(b) ? 1 : sortKey(a) < sortKey(b) ? -1 : 0;
        } else {
          return sortKey(a) > sortKey(b) ? -1 : sortKey(a) < sortKey(b) ? 1 : 0;
        }
      })
      .slice(paginationOffset, paginationOffset + paginationSize);
  }, [files, paginationOffset, paginationSize, filterString, sortColumn, sortKey, sortDirection]);

  return (
    <div className="card">
      <div className="card-header text-light font-weight-bold container-fluid">
        <div className="row">
          <div className="col-12">My Files</div>
        </div>
      </div>
      <div>
        <div className="row m-0">
          <div className="col"></div>
          <div className="col-auto mt-3">
            {files && (
              <PhGridFilter
                filterString={filterString}
                setFilterString={setFilterString}
                setPaginationOffset={setPaginationOffset}
              />
            )}
          </div>
        </div>
        <table className="table table-striped container-fluid" style={{ padding: 0, margin: 0 }}>
          <thead>
            <tr>
              <th scope="col">
                <PhGridSort
                  columnLabel="File Name"
                  columnName={(d) => d?.description}
                  config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                />
              </th>
              <th scope="col">
                <PhGridSort
                  columnLabel="Job Id"
                  columnName={(d) => d?.id}
                  config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                />
              </th>
              <th scope="col">
                <PhGridSort
                  columnLabel="Size"
                  columnName={(d) => (d.file_size ? parseInt(d.file_size, 10) : 0)}
                  config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                />
              </th>
              <th scope="col">
                <PhGridSort
                  columnLabel="Created"
                  columnName={(d) => d?.created_date || 0}
                  config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                />
              </th>
              <th scope="col">
                <PhGridSort
                  columnLabel="Expires"
                  columnName={(d) => d?.created_date || 0}
                  config={{ sortKey, setSortKey, sortDirection, setSortDirection, sortColumn, setSortColumn }}
                />
              </th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {!files && (
              <tr>
                <td colSpan="6" className="text-center">
                  <Spinner />
                </td>
              </tr>
            )}
            {displayList.map((item) => {
              const expires = calcDaysTilPurge(item.created_date);
              const expired = expires === "expired";
              return (
                <tr key={item.id}>
                  <th scope="row">{item.description}</th>
                  <td>{item.id}</td>
                  <td>{item.file_size ? readableBytes(item.file_size) : "unavailable"}</td>
                  <td>{item.created_date ? item.created_date.toLocaleDateString() : "-"}</td>
                  <td>{expires}</td>
                  <td>
                    <a
                      href={item.signed_url}
                      className={expired ? "btn btn-secondary disabled" : "btn btn-primary"}
                      tabIndex={expired ? -1 : undefined}
                      aria-disabled={expired ? "true" : undefined}
                      download
                    >
                      Download
                    </a>
                    {item.canBeVisualized && (
                      <Link to={`/queryvisualization/${item.id}`} 
                        className={`btn ${expired ? "btn-secondary disabled" : "btn-success"} m-2`} >
                        Visualize
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {files && (
          <div className="row m-0 justify-content-end" style={{ paddingRight: ".75rem" }}>
            <PhGridPagination
              fullList={files}
              paginationOffset={paginationOffset}
              setPaginationOffset={setPaginationOffset}
              setPaginationSize={setPaginationSize}
              paginationSize={paginationSize}
              filterString={filterString}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MyFilesContainer;
